// Excel на справката „Изписвания": редовете такива, каквито са на екрана —
// филтрираните, намерените и подредените (`shown`), с всички колони на
// широкия изглед (Вид и причина разделени, коментарът отделно), и последен ред
// „Общо за периода · без анулирани" със сумите от реда „Общо" на таблицата.
// Анулираните остават, със статус и причината за анулиране.
import type { TFunction } from "i18next";
import type { Writeoff } from "../../../../api/inventory";
import type { ExcelColumn } from "../../../../utils/excelExport";
import { fmtDateTime } from "../../components/dateTime";
import { excelMoney } from "../../components/list/excelCells";
import { isWriteoffAnnulled, sumActiveWriteoffs } from "../../writeoff/components/writeoffMeta";

export type WriteoffExcelRow = {
  // № на документа; в реда „Общо" — етикетът му
  number: number | string;
  date: string;
  kind: string;
  reason: string;
  comment: string;
  status: string;
  warehouse: string;
  user: string;
  products: number | null;
  net: number;
  gross: number;
  annulReason: string;
};

export type WriteoffExcelLabels = {
  kindOf: (writeoff: Writeoff) => string;
  // етикетът на причината (или „Без причина")
  reasonOf: (writeoff: Writeoff) => string;
  annulled: string;
  total: string;
};

export const writeoffExcelRows = (writeoffs: Writeoff[], labels: WriteoffExcelLabels): WriteoffExcelRow[] => {
  const rows = writeoffs.map((writeoff): WriteoffExcelRow => {
    const annulled = isWriteoffAnnulled(writeoff);
    return {
      number: writeoff.id,
      date: fmtDateTime(writeoff.date),
      kind: labels.kindOf(writeoff),
      reason: labels.reasonOf(writeoff),
      comment: writeoff.comment ?? "",
      status: annulled ? labels.annulled : "",
      warehouse: writeoff.warehouseName ?? "",
      user: writeoff.createdByName ?? "",
      products: writeoff.productsCount ?? 0,
      net: excelMoney(writeoff.totalValueNoVat),
      gross: excelMoney(writeoff.totalValueWithVat),
      annulReason: annulled ? (writeoff.deleteReason ?? "") : "",
    };
  });
  if (!rows.length) return rows;
  const totals = sumActiveWriteoffs(writeoffs);
  return [
    ...rows,
    {
      number: labels.total,
      date: "",
      kind: "",
      reason: "",
      comment: "",
      status: "",
      warehouse: "",
      user: "",
      products: null,
      net: excelMoney(totals.net),
      gross: excelMoney(totals.gross),
      annulReason: "",
    },
  ];
};

export const writeoffExcelColumns = (t: TFunction): ExcelColumn<WriteoffExcelRow>[] => [
  { header: "№", value: (row) => row.number },
  { header: t("inventoryPage.colDateTime"), value: (row) => row.date, width: 17 },
  { header: t("inventoryPage.cfKind"), value: (row) => row.kind },
  { header: t("inventoryPage.colReason"), value: (row) => row.reason, width: 24 },
  { header: t("inventoryPage.colComment"), value: (row) => row.comment, width: 28 },
  { header: t("inventoryPage.colStatus"), value: (row) => row.status },
  { header: t("inventoryPage.colWarehouse"), value: (row) => row.warehouse },
  { header: t("inventoryPage.filterEnteredBy"), value: (row) => row.user },
  { header: t("inventoryPage.colProducts"), value: (row) => row.products },
  { header: t("inventoryPage.colNet"), value: (row) => row.net },
  { header: t("inventoryPage.colGross"), value: (row) => row.gross },
  { header: t("inventoryPage.tbColAnnulReason"), value: (row) => row.annulReason, width: 28 },
];
